/**
 * Execution History List Component
 *
 * Displays a list of past workflow executions.
 * Clicking an execution applies its status to the canvas nodes.
 */

import { useEffect } from 'react';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import { useExecutions, useExecution } from '../hooks/useImageWorkflows';
import { normalizeExecutionSteps } from '../utils/executionSteps';
import {
  useWorkflowStore,
  useSelectedExecutionId,
} from '../hooks/useWorkflowStore';
import type { WorkflowExecution, ExecutionStatus } from '@/types/imageWorkflow';

interface ExecutionHistoryListProps {
  workflowId: string | null;
}

// UUID validation regex (backend requires UUID format)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Check if workflowId is a valid UUID
const isValidWorkflowId = (id: string | null): boolean => {
  if (!id || id.trim().length === 0) return false;
  const isUUID = UUID_REGEX.test(id);
  if (!isUUID) {
    console.warn('[ExecutionHistoryList] Invalid workflow ID format:', id);
  }
  return isUUID;
};

export function ExecutionHistoryList({ workflowId }: ExecutionHistoryListProps) {
  const { messages, language } = useI18n();
  const t = messages.imageWorkflows;
  const tc = messages.common;

  const selectedExecutionId = useSelectedExecutionId();
  const { selectExecution, applyExecutionToCanvas } = useWorkflowStore();

  // Only fetch if workflowId exists
  const hasValidWorkflowId = isValidWorkflowId(workflowId);

  // Fetch executions for this workflow
  const { data, isLoading, error, refetch } = useExecutions(
    hasValidWorkflowId ? { workflow_id: workflowId!, limit: 20 } : undefined,
    { enabled: hasValidWorkflowId }
  );

  // Fetch selected execution details with user runs
  const { data: selectedExecution, isLoading: isLoadingDetails } = useExecution(
    selectedExecutionId,
    { includeRuns: true }
  );

  // When execution details load, apply to canvas
  const handleSelectExecution = async (executionId: string) => {
    if (selectedExecutionId === executionId) {
      // Deselect if clicking same one
      selectExecution(null);
      return;
    }
    selectExecution(executionId);
  };

  // Effect: apply steps to canvas when execution details are loaded
  useEffect(() => {
    if (selectedExecution && selectedExecution.user_runs?.[0]?.steps) {
      const steps = normalizeExecutionSteps(selectedExecution.user_runs[0].steps);
      applyExecutionToCanvas(steps);
    }
  }, [selectedExecution, applyExecutionToCanvas]);

  const formatRelativeTime = (dateString: string) => {
    const date = parseBackendDateTime(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const rt = t.executionHistory?.relativeTime;

    if (diffMins < 1) return rt?.justNow || 'Just now';
    if (diffMins < 60)
      return (rt?.minutesAgo || '{n}m ago').replace('{n}', String(diffMins));
    if (diffHours < 24)
      return (rt?.hoursAgo || '{n}h ago').replace('{n}', String(diffHours));
    if (diffDays < 7)
      return (rt?.daysAgo || '{n}d ago').replace('{n}', String(diffDays));
    return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusIcon = (status: ExecutionStatus) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'failed':
        return '✗';
      case 'running':
        return '⏳';
      case 'pending':
        return '○';
      case 'partial':
        return '⚠';
      case 'cancelled':
        return '⊘';
      default:
        return '○';
    }
  };

  const getStatusColor = (status: ExecutionStatus) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'running':
        return 'text-blue-600';
      case 'pending':
        return 'text-gray-400';
      case 'partial':
        return 'text-yellow-600';
      case 'cancelled':
        return 'text-gray-500';
      default:
        return 'text-gray-400';
    }
  };

  const renderExecutionItem = (execution: WorkflowExecution) => {
    const isSelected = selectedExecutionId === execution.id;
    // Support both new dual-layer structure and old flat structure
    const definition = execution.workflow_snapshot?.definition;
    const nodeCount = definition?.config?.nodes?.length ?? definition?.nodes?.length ?? 0;
    const completedNodes = execution.completed_users > 0 ? nodeCount : 0;

    return (
      <div
        key={execution.id}
        onClick={() => handleSelectExecution(execution.id)}
        className={`
          p-3 rounded-lg border cursor-pointer transition-all
          ${isSelected
            ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-lg ${getStatusColor(execution.status)}`}>
              {getStatusIcon(execution.status)}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">
                  {t?.executionHistory?.executionLabel || 'Execution'} #{execution.id.slice(0, 8)}
                </span>
                {execution.status === 'running' && (
                  <span className="animate-pulse text-xs text-blue-600">
                    {tc?.loading || 'Loading...'}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {nodeCount > 0 && (
                  <span className="mr-2">
                    {completedNodes}/{nodeCount} {t?.executionHistory?.nodes || 'nodes'}
                  </span>
                )}
                {execution.total_duration_ms && (
                  <span className="mr-2">{formatDuration(execution.total_duration_ms)}</span>
                )}
                <span>{formatRelativeTime(execution.created_at)}</span>
              </div>
            </div>
          </div>
          <span className="text-gray-400 text-sm">
            {isSelected ? '▼' : '▶'}
          </span>
        </div>

        {/* Loading indicator when fetching details */}
        {isSelected && isLoadingDetails && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="animate-pulse flex items-center gap-2 text-xs text-gray-500">
              <span className="animate-spin">⏳</span>
              {tc?.loading || 'Loading details...'}
            </div>
          </div>
        )}

        {/* Error message if execution failed */}
        {isSelected && execution.error_summary && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {execution.error_summary}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!hasValidWorkflowId) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        {t?.executionHistory?.saveFirst || 'Save the workflow to view execution history'}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    // Extract error message from various error formats
    const getErrorMessage = (err: unknown): string => {
      if (err instanceof Error) return err.message;
      if (typeof err === 'string') return err;
      if (err && typeof err === 'object') {
        // Handle Axios error format
        const axiosErr = err as { response?: { data?: { detail?: string; message?: string } }; message?: string };
        if (axiosErr.response?.data?.detail) return axiosErr.response.data.detail;
        if (axiosErr.response?.data?.message) return axiosErr.response.data.message;
        if (axiosErr.message) return axiosErr.message;
      }
      return 'Unknown error';
    };

    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-sm mb-2">
          {tc?.error || 'Error'}: {getErrorMessage(error)}
        </div>
        <button
          onClick={() => refetch()}
          className="text-primary-600 hover:text-primary-700 text-sm"
        >
          {tc?.retry || 'Retry'}
        </button>
      </div>
    );
  }

  const executions = data?.executions ?? [];

  if (executions.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-2">📭</div>
        <div className="text-gray-500 text-sm">
          {t?.executionHistory?.noHistory || 'No execution history'}
        </div>
        <div className="mt-2 text-xs text-gray-400">
          {t?.executionHistory?.runFirst || 'Run the workflow to see execution history'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          {t?.executionHistory?.title || 'Execution History'} ({executions.length})
        </span>
        <button
          onClick={() => refetch()}
          className="text-xs text-gray-500 hover:text-gray-700"
          disabled={isLoading}
        >
          {tc?.refresh || 'Refresh'}
        </button>
      </div>
      {executions.map(renderExecutionItem)}
    </div>
  );
}

export default ExecutionHistoryList;
